import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import HotRankingList from "@/components/feature/HotRankingList";
import ArticleHeader from "@/pages/article/components/ArticleHeader";
import ArticleBody from "@/pages/article/components/ArticleBody";
import CommentList from "@/pages/article/components/CommentList";
import RelatedArticles from "@/pages/article/components/RelatedArticles";
import { useSeo } from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { SITE_NAME, SITE_URL } from "@/utils/format";

export default function ArticlePage() {
  const { id = "" } = useParams();
  const article = articles.find((item) => item.id === id);
  const category = categories.find((item) => item.slug === article?.category);

  const related = article
    ? articles
        .filter((item) => item.id !== article.id)
        .sort((a, b) => {
          const aSame = a.category === article.category ? 1 : 0;
          const bSame = b.category === article.category ? 1 : 0;
          if (aSame !== bSame) return bSame - aSame;
          return b.hot - a.hot;
        })
        .slice(0, 3)
    : [];

  const path = `/article/${id}`;
  const jsonLd = article
    ? [
        {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: article.title,
          description: article.summary,
          image: article.cover,
          inLanguage: "zh-CN",
          keywords: article.tags.join(","),
          articleSection: category?.name ?? "热瓜",
          author: {
            "@type": "Person",
            name: article.author,
          },
          publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            url: `${SITE_URL}/`,
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${SITE_URL}${path}`,
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: `${SITE_URL}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: category?.name ?? "瓜田",
              item: `${SITE_URL}/category/${article.category}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
              item: `${SITE_URL}${path}`,
            },
          ],
        },
      ]
    : undefined;

  useSeo({
    title: article
      ? `${article.title} - 瓜友会网吃瓜现场`
      : `爆料不存在 - ${SITE_NAME}`,
    description: article
      ? `${article.summary} 瓜友会网持续跟进${category?.name ?? "热瓜"}最新进展，蹲后续、看网友热评就来吃瓜现场。`
      : "你访问的爆料不存在或已下线，返回瓜友会网首页继续吃瓜。",
    keywords: article
      ? `瓜友会网,${article.title},${article.tags.join(",")},吃瓜现场,今日爆料`
      : "瓜友会网,吃瓜现场",
    path,
    ogType: "article",
    jsonLd,
  });

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        {article ? (
          <>
            <div className="w-full px-4 md:px-8 py-4 md:py-5 bg-background-100 border-b border-background-200">
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  {
                    label: category?.name ?? "瓜田",
                    to: `/category/${article.category}`,
                  },
                  { label: article.title },
                ]}
              />
            </div>

            <div className="w-full px-4 md:px-8 py-8 md:py-12">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
                <div className="lg:col-span-2 min-w-0">
                  <ArticleHeader article={article} />
                  <div className="mt-6 md:mt-8">
                    <ArticleBody article={article} />
                  </div>
                  <CommentList />
                </div>

                <div className="lg:col-span-1">
                  <div className="lg:sticky lg:top-24 flex flex-col gap-5">
                    <HotRankingList articles={articles} limit={6} />

                    <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                      <h3 className="text-base font-bold text-foreground-950 mb-3">
                        按圈层找瓜
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {categories.map((cat) => (
                          <Link
                            key={cat.slug}
                            to={`/category/${cat.slug}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-xs text-foreground-600 hover:border-primary-200 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className={cat.icon} aria-hidden="true"></i>
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-lg bg-secondary-100 border border-secondary-200 p-5">
                      <h3 className="text-base font-bold text-secondary-950 mb-2">
                        有新进展不想错过？
                      </h3>
                      <p className="text-sm text-secondary-900/80 leading-relaxed">
                        留下邮箱，我们把这条瓜的后续整理好发给你。
                      </p>
                      <Link
                        to="/about#submit"
                        className="mt-4 inline-flex items-center gap-1 h-10 px-4 rounded-full bg-secondary-600 text-background-50 text-sm whitespace-nowrap hover:bg-secondary-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-notification-3-line" aria-hidden="true"></i>
                        订阅进展
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <RelatedArticles articles={related} />
            </div>
          </>
        ) : (
          <div className="w-full px-4 md:px-8 py-24 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-primary-50 text-primary-500 flex items-center justify-center mb-5">
              <i className="ri-error-warning-line text-3xl" aria-hidden="true"></i>
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
              这条爆料找不到了
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-500">
              可能已经下线或链接有误，回首页看看今天的其他热瓜吧。
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-1 h-11 px-6 rounded-full bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line" aria-hidden="true"></i>
              返回首页
            </Link>
          </div>
        )}
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}